import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FileText,
  Download,
  Plus,
  Loader2,
  Calendar as CalendarIcon,
  Award,
  CheckCircle2,
  AlertCircle,
  Eye,
  ArrowLeft,
  Trash2,
  Building2,
  User,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatDateTimeBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function backendBaseUrl() {
  // No same-origin proxy do CEAP, REACT_APP_BACKEND_URL aponta para o domínio do app.
  // Como o /api/* já é roteado pelo ingress, basta retornar essa raiz.
  return (
    (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) ||
    window.location.origin
  ).replace(/\/$/, "");
}

export default function PrestacaoContas() {
  const { id: contractId } = useParams();
  const { user } = useAuth();

  const [contract, setContract] = useState(null);
  const [history, setHistory] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Generate dialog
  const [open, setOpen] = useState(false);
  const today = new Date();
  const [form, setForm] = useState({
    month: today.getMonth() + 1,
    year: today.getFullYear(),
  });
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [step, setStep] = useState("select"); // select | preview

  // Melhoria 3 — Upload manual de prestação antiga
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    month: today.getMonth() + 1,
    year: today.getFullYear(),
    file: null,
    notes: "",
  });
  const [uploadConflict, setUploadConflict] = useState(null); // {existing_id, message, ...}

  const refresh = async () => {
    const [c, h] = await Promise.all([
      api.get(`/contracts/${contractId}`),
      api.get(`/prestacoes/contract/${contractId}`),
    ]);
    setContract(c.data);
    setHistory(h.data);
  };

  useEffect(() => {
    refresh().catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const canGenerate =
    user?.role === ROLES.DIRETORA ||
    user?.role === ROLES.SECRETARIA ||
    user?.role === ROLES.CLIENTE;
  const canDelete = user?.role === ROLES.DIRETORA;

  const years = useMemo(() => {
    const y = today.getFullYear();
    return [y - 1, y, y + 1];
  }, [today]);

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(
        `/prestacoes/contract/${contractId}/preview`,
        { params: { month: form.month, year: form.year } },
      );
      setPreviewData(data);
      setStep("preview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar prévia.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/prestacoes/contract/${contractId}/generate`,
        { month: form.month, year: form.year },
      );
      toast.success("Prestação gerada e disponível no histórico.");
      setOpen(false);
      setStep("select");
      setPreviewData(null);
      refresh();
      // Open PDF in a new tab right away
      const token = localStorage.getItem("ceap_token");
      const url = `${backendBaseUrl()}${data.pdf_url}`;
      // Open with auth header via fetch + blob
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      } catch {
        // fallback: ignore
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar PDF.");
    } finally {
      setGenerating(false);
    }
  };

  // Melhoria 3 — Upload manual de prestação antiga (ou Plano B caso gerador automático falhe)
  const submitUpload = async (force = false) => {
    if (!uploadForm.file) {
      return toast.error("Selecione um arquivo PDF.");
    }
    if (uploadForm.file.type !== "application/pdf" &&
        !uploadForm.file.name?.toLowerCase().endsWith(".pdf")) {
      return toast.error("Apenas arquivos PDF são aceitos.");
    }
    if (uploadForm.file.size > 15 * 1024 * 1024) {
      return toast.error("Arquivo excede o limite de 15 MB.");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("month", String(uploadForm.month));
      fd.append("year", String(uploadForm.year));
      fd.append("file", uploadForm.file);
      if (uploadForm.notes?.trim()) fd.append("notes", uploadForm.notes.trim());
      if (force) fd.append("force", "true");
      const { data } = await api.post(
        `/prestacoes/contract/${contractId}/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        data.warning === "duplicate_forced"
          ? "Prestação manual enviada (sobrescrevendo duplicata)."
          : "Prestação manual enviada com sucesso."
      );
      setUploadOpen(false);
      setUploadConflict(null);
      setUploadForm({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        file: null,
        notes: "",
      });
      refresh();
    } catch (err) {
      const status = err.response?.status;
      const payload = err.response?.data?.detail;
      // Conflito 409 — duplicata. Exibimos prompt no próprio dialog.
      if (status === 409 && payload && typeof payload === "object" && payload.duplicate) {
        setUploadConflict(payload);
      } else {
        toast.error(
          (payload && typeof payload === "string" ? payload : payload?.message) ||
          "Erro ao enviar prestação manual."
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const downloadPdf = async (entry) => {
    const token = localStorage.getItem("ceap_token");
    const url = `${backendBaseUrl()}${entry.pdf_url}`;
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `prestacao_${entry.year}-${String(entry.month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Falha ao baixar PDF.");
    }
  };

  const viewPdf = async (entry) => {
    const token = localStorage.getItem("ceap_token");
    const url = `${backendBaseUrl()}${entry.pdf_url}`;
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch {
      toast.error("Falha ao abrir PDF.");
    }
  };

  const deletePrestacao = async (entry) => {
    if (!window.confirm(`Apagar a prestação de ${MONTHS[entry.month - 1]}/${entry.year}? Esta ação não pode ser desfeita.`)) {
      return;
    }
    setDeletingId(entry.id);
    try {
      await api.delete(`/prestacoes/${entry.id}`);
      toast.success("Prestação apagada.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao apagar.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!contract || !history) return <Skeleton className="h-96" />;

  const basePath =
    user?.role === ROLES.DIRETORA ? "/diretora" :
    user?.role === ROLES.SECRETARIA ? "/secretaria" :
    user?.role === ROLES.CONSULTOR ? "/consultor" :
    user?.role === ROLES.JURIDICO ? "/juridico" : "/portal";

  return (
    <div className="space-y-6" data-testid="prestacao-page">
      <Link
        to={`${basePath}/contratos/${contractId}`}
        className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar ao contrato
      </Link>

      {/* Header */}
      <Card className="card-soft overflow-hidden">
        <div className="bg-gradient-to-br from-midnight to-ink text-white px-6 py-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <Badge className="bg-white/15 text-white border border-white/20 hover:bg-white/20 mb-2">
                <Award className="w-3 h-3 mr-1" /> Prestação de Contas
              </Badge>
              <h1 className="text-2xl font-semibold" data-testid="prestacao-title">
                {contract.product_name}
              </h1>
              <div className="text-sm text-white/85 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" /> {contract.client_name}
                </span>
                {contract.consultant_name && (
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4" /> {contract.consultant_name}
                  </span>
                )}
              </div>
            </div>
            {canGenerate && (
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    setStep("select");
                    setPreviewData(null);
                    setOpen(true);
                  }}
                  size="lg"
                  className="bg-gold text-ink hover:bg-gold-deep hover:text-white shadow-md"
                  data-testid="generate-prestacao-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  GERAR PRESTAÇÃO DE CONTAS
                </Button>
                <Button
                  onClick={() => {
                    setUploadConflict(null);
                    setUploadOpen(true);
                  }}
                  size="lg"
                  variant="outline"
                  className="border-midnight text-midnight hover:bg-midnight/5"
                  data-testid="upload-prestacao-button"
                  title="Upload manual de prestação antiga (PDF) — útil para registros anteriores à plataforma ou como plano B se a geração automática falhar."
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Upload de Prestação Antiga
                </Button>
              </div>
            )}
          </div>
        </div>

        <CardContent className="p-4 text-sm text-slate-700">
          <p>
            Gere o documento oficial em PDF, timbrado, com a consolidação
            automática das reuniões realizadas, documentos publicados e
            providências concluídas no mês de referência. Cada PDF gerado
            fica salvo permanentemente no histórico abaixo.
          </p>
        </CardContent>
      </Card>

      {/* Histórico */}
      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-midnight" />
            Histórico de prestações geradas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              Nenhuma prestação gerada para este contrato ainda.
              {canGenerate && (
                <div className="mt-3">
                  <Button
                    onClick={() => setOpen(true)}
                    variant="outline"
                    size="sm"
                    data-testid="generate-prestacao-empty"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Gerar primeira prestação
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-600">
                  <th className="px-4 py-3">Mês de referência</th>
                  <th className="px-4 py-3">Gerado em</th>
                  <th className="px-4 py-3">Gerado por</th>
                  <th className="px-4 py-3">Tamanho</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100" data-testid={`prestacao-row-${h.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{h.month_label} / {h.year}</span>
                        {h.source === "manual" && (
                          <Badge
                            className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]"
                            data-testid={`prestacao-source-manual-${h.id}`}
                          >
                            Upload manual
                          </Badge>
                        )}
                      </div>
                      {h.notes && (
                        <div className="text-[11px] text-slate-500 mt-1 italic line-clamp-2">
                          {h.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {formatDateTimeBR(h.generated_at)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {h.generated_by_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {h.pdf_size ? `${Math.round(h.pdf_size / 1024)} KB` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewPdf(h)}
                          data-testid={`view-prestacao-${h.id}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Visualizar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(h)}
                          data-testid={`download-prestacao-${h.id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Baixar
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={deletingId === h.id}
                            onClick={() => deletePrestacao(h)}
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            data-testid={`delete-prestacao-${h.id}`}
                          >
                            {deletingId === h.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <Dialog open={open} onOpenChange={(v) => !generating && setOpen(v)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {step === "select" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-midnight" /> Gerar prestação de contas
                </DialogTitle>
                <DialogDescription>
                  Selecione o mês de referência. O sistema consolida automaticamente
                  os dados do período. Você verá uma prévia antes de gerar o PDF.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mês *</Label>
                  <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: Number(v) })}>
                    <SelectTrigger data-testid="presta-month-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ano *</Label>
                  <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: Number(v) })}>
                    <SelectTrigger data-testid="presta-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  onClick={fetchPreview}
                  disabled={previewLoading}
                  className="bg-midnight hover:bg-ink text-white"
                  data-testid="presta-preview-button"
                >
                  {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Visualizar prévia
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "preview" && previewData && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-midnight" /> Prévia da prestação — {previewData.month_label} / {previewData.year}
                </DialogTitle>
                <DialogDescription>
                  Confira os dados consolidados. Se estiver correto, confirme para gerar o PDF oficial.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <section className="border border-slate-200 rounded-md p-4">
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Identificação do contrato</h3>
                  <dl className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-1 text-sm">
                    <dt className="text-slate-600">Cliente:</dt>
                    <dd className="text-slate-900 font-medium">{previewData.client.name}</dd>
                    <dt className="text-slate-600">Município:</dt>
                    <dd className="text-slate-900">{previewData.client.city} / {previewData.client.state}</dd>
                    {(previewData.client.type_label || previewData.client.type) && (
                      <>
                        <dt className="text-slate-600">Tipo de cliente:</dt>
                        <dd className="text-slate-900">{previewData.client.type_label || previewData.client.type}</dd>
                      </>
                    )}
                    <dt className="text-slate-600">Produto:</dt>
                    <dd className="text-slate-900">{previewData.product.name}</dd>
                    <dt className="text-slate-600">Início do contrato:</dt>
                    <dd className="text-slate-900">{previewData.contract_start_date?.slice(0, 10)}</dd>
                    <dt className="text-slate-600">Consultor:</dt>
                    <dd className="text-slate-900">{previewData.consultant_name}</dd>
                  </dl>
                </section>

                <section className="border border-slate-200 rounded-md p-4">
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-3">Resumo do período</h3>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                      <div className="text-2xl font-semibold text-blue-900">{previewData.totals.meetings}</div>
                      <div className="text-xs text-blue-700 uppercase tracking-wide">Reuniões realizadas</div>
                    </div>
                    <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                      <div className="text-2xl font-semibold text-emerald-900">{previewData.totals.documents}</div>
                      <div className="text-xs text-emerald-700 uppercase tracking-wide">Docs publicados</div>
                    </div>
                  </div>
                </section>

                {previewData.activities.meetings.length > 0 && (
                  <section className="border border-slate-200 rounded-md p-4">
                    <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Reuniões realizadas</h3>
                    <ul className="space-y-2 text-sm">
                      {previewData.activities.meetings.map((m) => (
                        <li key={m.id} className="text-slate-700">
                          <div className="flex items-start gap-2 flex-wrap">
                            <CalendarIcon className="inline w-3 h-3 mt-1 text-slate-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div>
                                <strong>{m.title}</strong> — {formatDateTimeBR(m.scheduled_at)}{" "}
                                <span className="text-slate-500">({m.duration_minutes} min)</span>
                              </div>
                              {m.minutes_published && m.minutes_text ? (
                                <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  ✓ Ata publicada (será incluída no PDF)
                                </span>
                              ) : (
                                <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  Ata pendente
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {previewData.activities.documents.length > 0 && (
                  <section className="border border-slate-200 rounded-md p-4">
                    <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Documentos publicados</h3>
                    <ul className="space-y-1 text-sm">
                      {previewData.activities.documents.map((d) => (
                        <li key={d.id} className="text-slate-700">
                          <FileText className="inline w-3 h-3 mr-1 text-slate-500" />
                          <strong>{d.title}</strong>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {previewData.totals.meetings === 0 &&
                  previewData.totals.documents === 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900 inline-flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Nenhuma atividade encontrada neste mês. O PDF será gerado mostrando
                        explicitamente que não houve movimentação no período.
                      </span>
                    </div>
                  )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep("select")} disabled={generating}>
                  Voltar
                </Button>
                <Button
                  onClick={confirmGenerate}
                  disabled={generating}
                  className="bg-gold text-ink hover:bg-gold-deep hover:text-white"
                  data-testid="presta-confirm-generate"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Gerar PDF oficial
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Melhoria 3 — Dialog de upload manual de prestação antiga */}
      <Dialog
        open={uploadOpen}
        onOpenChange={(v) => {
          setUploadOpen(v);
          if (!v) setUploadConflict(null);
        }}
      >
        <DialogContent
          className="max-w-lg"
          data-testid="upload-prestacao-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-midnight" />
              Upload manual de prestação
            </DialogTitle>
            <DialogDescription>
              Envie um PDF de prestação antiga (anterior à plataforma) ou use
              como plano B caso a geração automática apresente problema.
              O documento aparecerá no histórico marcado como{" "}
              <strong>Upload manual</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Mês de referência</Label>
                <Select
                  value={String(uploadForm.month)}
                  onValueChange={(v) =>
                    setUploadForm({ ...uploadForm, month: Number(v) })
                  }
                >
                  <SelectTrigger data-testid="upload-month-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((label, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {String(i + 1).padStart(2, "0")} — {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Ano de referência</Label>
                <Select
                  value={String(uploadForm.year)}
                  onValueChange={(v) =>
                    setUploadForm({ ...uploadForm, year: Number(v) })
                  }
                >
                  <SelectTrigger data-testid="upload-year-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 8 }, (_, i) => today.getFullYear() - 5 + i).map(
                      (y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">
                Arquivo PDF <span className="text-red-600">*</span>
              </Label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) =>
                  setUploadForm({
                    ...uploadForm,
                    file: e.target.files?.[0] || null,
                  })
                }
                className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-midnight file:text-white hover:file:bg-ink cursor-pointer"
                data-testid="upload-file-input"
              />
              {uploadForm.file && (
                <p className="text-[11px] text-slate-500 mt-1">
                  <strong>{uploadForm.file.name}</strong> —{" "}
                  {Math.round(uploadForm.file.size / 1024)} KB
                </p>
              )}
              <p className="text-[10px] text-slate-400 mt-1">
                Limite: 15 MB. Apenas arquivos PDF.
              </p>
            </div>

            <div>
              <Label className="text-xs text-slate-600">
                Observação (opcional)
              </Label>
              <textarea
                value={uploadForm.notes}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, notes: e.target.value })
                }
                rows={2}
                placeholder="Ex.: prestação gerada manualmente antes da implantação da plataforma"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-midnight"
                data-testid="upload-notes-input"
              />
            </div>

            {uploadConflict && (
              <div
                className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 space-y-2"
                data-testid="upload-conflict-alert"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Já existe prestação para este mês</p>
                    <p className="text-xs mt-1">{uploadConflict.message}</p>
                    {uploadConflict.existing_source && (
                      <p className="text-[10px] mt-1 italic">
                        Existente: {uploadConflict.existing_source === "manual" ? "Upload manual" : "Gerada automaticamente"}
                        {uploadConflict.existing_generated_at &&
                          ` em ${formatDateTimeBR(uploadConflict.existing_generated_at)}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadOpen(false);
                setUploadConflict(null);
              }}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => submitUpload(!!uploadConflict)}
              disabled={uploading || !uploadForm.file}
              className="bg-midnight hover:bg-ink text-white"
              data-testid="upload-confirm-button"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              {uploadConflict ? "Continuar mesmo assim" : "Enviar PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
